import { CPP_QPP_EI_RATES } from '../constants/province-data';
import { CalculatorFormData, CostCalculationResult, CalculatorProvince } from '../types/calculator';
import { ProvincialTaxVersionResponse } from '../../hooks/use-tax-api';
import { calculateAllEmploymentContributions } from './api-cost-calculations';

/**
 * Calculate CPP or QPP contribution based on province (LEGACY - using mock data)
 */
export const calculateCppQppContribution = (basePay: number, isQuebec: boolean): number => {
  const rates = isQuebec ? CPP_QPP_EI_RATES.QPP : CPP_QPP_EI_RATES.CPP;
  return Math.min(basePay * (rates.rate / 100), rates.maxContribution);
};

/**
 * Calculate Employment Insurance contribution (LEGACY - using mock data)
 */
export const calculateEiContribution = (basePay: number): number => {
  return Math.min(basePay * (CPP_QPP_EI_RATES.EI.rate / 100), CPP_QPP_EI_RATES.EI.maxContribution);
};

/**
 * Calculate WSIB/WCB contribution
 */
export const calculateWsibContribution = (basePay: number, wsibRate: number): number => {
  return basePay * (wsibRate / 100);
};

/**
 * Calculate Employer Health Tax contribution
 */
export const calculateEhtContribution = (
  basePay: number, 
  province: CalculatorProvince, 
  includeEHT: boolean, 
  annualPayroll: number
): number => {
  if (!province?.hasEHT || !includeEHT) return 0;
  if (annualPayroll < province.ehtThreshold) return 0;
  return basePay * (province.ehtRate / 100);
};



/**
 * Calculate productive hours and effective hourly rates
 */
export const calculateHourlyRates = (
  totalCost: number,
  basePay: number,
  totalPaidTimeOffDays: number,
  hoursPerDay: number = 8
) => {
  const workDaysPerYear = 260; // 52 weeks * 5 days
  const workHoursPerYear = workDaysPerYear * hoursPerDay; // Dynamic based on hours per day
  const productiveHours = workHoursPerYear - totalPaidTimeOffDays * hoursPerDay;
  
  return {
    productiveHours,
    effectiveHourlyRate: totalCost / productiveHours,
    standardHourlyRate: basePay / workHoursPerYear,
    burdenedHourlyRate: totalCost / workHoursPerYear
  };
};

/**
 * Main cost calculation function - now supports both mock data and real API data
 * @param form - Form data
 * @param province - Province information
 * @param wsibRate - WSIB rate
 * @param taxData - Optional real tax data from API
 */
export const calculateTotalEmployerCost = (
  form: CalculatorFormData,
  province: CalculatorProvince,
  wsibRate: number,
  taxData?: ProvincialTaxVersionResponse | null
): CostCalculationResult => {
  const basePay = Number.parseFloat(form.salary) || 0;
  
  // Parse form values
  const vacationDays = Number.parseFloat(form.vacationDays) || 0;
  const sickDays = Number.parseFloat(form.sickDays) || 0;
  const publicHolidays = Number.parseFloat(form.numPublicHolidays) || 0;
  const otherPaidLeave = Number.parseFloat(form.otherPaidLeave) || 0;
  const benefits = Number.parseFloat(form.benefits) || 0;
  const bonusesIncentives = Number.parseFloat(form.bonusesIncentives) || 0;
  const trainingOverhead = Number.parseFloat(form.trainingOverhead) || 0;
  const hoursPerDay = Number.parseFloat(form.hoursPerDay) || 8;

  // Calculate contributions - use real API data if available, otherwise fallback to mock data
  let cppQppContribution: number;
  let eiContribution: number;
  let ehtContribution: number;
  let pensionType: 'CPP' | 'QPP' | undefined;
  let pension1Contribution: number | undefined;
  let pension2Contribution: number | undefined;

  if (taxData) {
    console.log('Using real API data for tax calculations');
    const apiContributions = calculateAllEmploymentContributions(basePay, taxData);
    
    // Apply user selections for CPP/QPP and EI
    const isQuebec = form.province === "QC";
    const shouldApplyCpp = !isQuebec && form.applyCpp;
    const shouldApplyQpp = isQuebec && form.applyQpp;
    const shouldApplyEi = form.applyEi;
    
    cppQppContribution = (shouldApplyCpp || shouldApplyQpp) ? apiContributions.total : 0;
    eiContribution = shouldApplyEi ? apiContributions.employmentInsurance : 0;
    ehtContribution = apiContributions.employerHealthTax || 0;
    pensionType = apiContributions.pensionType as 'CPP' | 'QPP';
    pension1Contribution = (shouldApplyCpp || shouldApplyQpp) ? apiContributions.pension1 : 0;
    pension2Contribution = (shouldApplyCpp || shouldApplyQpp) ? apiContributions.pension2 : 0;
    
    console.log('API-based calculations with user toggles:', {
      pensionType,
      shouldApplyCpp,
      shouldApplyQpp,
      shouldApplyEi,
      pension1: pension1Contribution,
      pension2: pension2Contribution,
      totalPension: cppQppContribution,
      ei: eiContribution,
      eht: ehtContribution
    });
  } else {
    console.log('Falling back to mock data for tax calculations');
    const isQuebec = form.province === "QC";
    const shouldApplyCpp = !isQuebec && form.applyCpp;
    const shouldApplyQpp = isQuebec && form.applyQpp;
    const shouldApplyEi = form.applyEi;
    
    cppQppContribution = (shouldApplyCpp || shouldApplyQpp) ? calculateCppQppContribution(basePay, isQuebec) : 0;
    eiContribution = shouldApplyEi ? calculateEiContribution(basePay) : 0;
    ehtContribution = calculateEhtContribution(basePay, province, form.includeEHT, basePay);
    pensionType = isQuebec ? 'QPP' : 'CPP';
    pension1Contribution = (shouldApplyCpp || shouldApplyQpp) ? cppQppContribution : 0; // In mock data, we don't separate CPP1/CPP2
    pension2Contribution = 0;
    
    console.log('Mock data calculations with user toggles:', {
      pensionType,
      shouldApplyCpp,
      shouldApplyQpp,
      shouldApplyEi,
      totalPension: cppQppContribution,
      ei: eiContribution,
      eht: ehtContribution
    });
  }

  const wsibContribution = calculateWsibContribution(basePay, wsibRate);

  // For salaried employees, PTO costs are always $0 but we track days for productivity
  const totalPaidTimeOffDays = vacationDays + sickDays + publicHolidays + otherPaidLeave;

  // Calculate total cost (no PTO costs for salaried employees)
  const total = basePay + 
    cppQppContribution + 
    eiContribution + 
    wsibContribution + 
    ehtContribution + 
    benefits + 
    bonusesIncentives + 
    trainingOverhead;

  // Calculate hourly rates
  const hourlyRates = calculateHourlyRates(total, basePay, totalPaidTimeOffDays, hoursPerDay);

  return {
    basePay,
    cppQppContribution,
    pensionType,
    pension1Contribution,
    pension2Contribution,
    eiContribution,
    wsibContribution,
    ehtContribution,
    benefits,
    bonusesIncentives,
    trainingOverhead,
    total,
    totalPaidTimeOffDays,
    ...hourlyRates
  };
};

/**
 * Calculate the labour burden rate as a percentage
 */
export const calculateBurdenRate = (totalCost: number, basePay: number): number => {
  if (basePay === 0) return 0;
  return ((totalCost - basePay) / basePay) * 100;
}; 